/**
 * ZypernKompass — indicative tax savings calculator (DE GmbH vs CY Ltd Non-Dom).
 *
 * NOT tax or legal advice. Educational placeholders for conversion / orientation only.
 *
 * ---------------------------------------------------------------------------
 * MODEL SUMMARY (highly simplified — individual cases differ materially)
 * ---------------------------------------------------------------------------
 * Base B = max(0, profit_before_tax − geschäftsführer_salary) as taxable profit proxy.
 *
 * Germany (company layer):
 *   KSt = B × 15 %
 *   Solidaritätszuschlag = KSt × 5.5 %
 *   GewSt ≈ B × gew_rate (select: indicative effective placeholder per scenario / state)
 *
 * Germany (personal / distribution):
 *   Salary: indicative blended personal income tax on salary (not ESt splitting).
 *   Dividends (when “distribute”): min(distribution, profit_after_DE_corp) × 26.375 %
 *     as simplified Kapitalertrag layer (withholding-style).
 *
 * Cyprus:
 *   Corporate tax = B × 15 % (no German GewSt in this model).
 *   Salary: indicative lower effective rate placeholder for Cyprus residency mix.
 *   Non-Dom dividends: 0 % additional layer in this illustrative scenario when distributing.
 *
 * “Net to owner” (cash-style): net salary after indicative personal tax + net dividends
 * when distributing; dividends = 0 when retaining (cash comparison).
 *
 * ---------------------------------------------------------------------------
 */

/**
 * @param {number} salaryAnnual
 * @returns {number} Indicative German personal tax on salary only (EUR).
 */
function estimatePersonalTaxDE(salaryAnnual) {
  const s = Math.max(0, salaryAnnual);
  if (s <= 0) {
    return 0;
  }
  if (s <= 28_000) {
    return s * 0.26;
  }
  if (s <= 65_000) {
    return s * 0.36;
  }
  return s * 0.41;
}

/**
 * @param {number} salaryAnnual
 * @returns {number} Indicative Cyprus personal tax on salary (EUR).
 */
function estimatePersonalTaxCY(salaryAnnual) {
  const s = Math.max(0, salaryAnnual);
  if (s <= 0) {
    return 0;
  }
  return s * 0.14;
}

/**
 * German corporate stack on profit base B (before dividend distribution).
 *
 * @param {number} base
 * @param {number} gewRate Effective GewSt placeholder on same base (not legal Hebesatz).
 */
function germanCorporateTax(base, gewRate) {
  const b = Math.max(0, base);
  const kst = b * 0.15;
  const solz = kst * 0.055;
  const gew = b * gewRate;
  return kst + solz + gew;
}

/** Cyprus corporate tax only on base B. */
function cyprusCorporateTax(base) {
  return Math.max(0, base) * 0.15;
}

function fmtEur(value) {
  const n = Number.isFinite(value) ? Math.round(value) : 0;
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(n);
  }
  catch {
    return `${n.toLocaleString('de-DE')} €`;
  }
}

function fmtPct(value) {
  if (!Number.isFinite(value)) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)} %`;
}

/**
 * @param {object} params
 * @returns {object}
 */
function computeComparison(params) {
  const profit = Math.max(0, params.profit);
  const salary = Math.max(0, params.salary);
  const distributionInput = Math.max(0, params.distribution);
  const gewRate = params.gewRate;
  const distribute = params.mode === 'distribute';

  const base = Math.max(0, profit - salary);

  const corpDE = germanCorporateTax(base, gewRate);
  const profitAfterDE = Math.max(0, base - corpDE);

  const corpCY = cyprusCorporateTax(base);
  const profitAfterCY = Math.max(0, base - corpCY);

  const persDE = estimatePersonalTaxDE(salary);
  const salaryNetDE = Math.max(0, salary - persDE);

  const persCY = estimatePersonalTaxCY(salary);
  const salaryNetCY = Math.max(0, salary - persCY);

  let divTaxDE = 0;
  let divNetDE = 0;
  let divNetCY = 0;

  if (distribute) {
    const distCapDE = Math.min(distributionInput, profitAfterDE);
    divTaxDE = distCapDE * 0.26375;
    divNetDE = Math.max(0, distCapDE - divTaxDE);

    const distCapCY = Math.min(distributionInput, profitAfterCY);
    divNetCY = distCapCY;
  }

  const netOwnerDE = salaryNetDE + divNetDE;
  const netOwnerCY = salaryNetCY + divNetCY;

  const totalTaxDE = corpDE + persDE + divTaxDE;
  const totalTaxCY = corpCY + persCY;

  const savingsAbs = netOwnerCY - netOwnerDE;
  let savingsPct = 0;
  if (Math.abs(netOwnerDE) > 1) {
    savingsPct = (savingsAbs / Math.abs(netOwnerDE)) * 100;
  }

  const maxNet = Math.max(netOwnerDE, netOwnerCY, 1);
  const barDe = (netOwnerDE / maxNet) * 100;
  const barCy = (netOwnerCY / maxNet) * 100;

  return {
    netOwnerDE,
    netOwnerCY,
    totalTaxDE,
    totalTaxCY,
    savingsAbs,
    savingsPct,
    barDe,
    barCy,
    distribute,
  };
}

/**
 * @param {HTMLElement} root
 */
function initTaxCalculator(root) {
  const pid = root.dataset.paragraphId || '';
  const form = root.querySelector(`#zk-tax-calculator-form-${pid}`);
  const results = root.querySelector(`#zk-tax-calculator-results-${pid}`);
  if (!form || !results) {
    return;
  }

  const outs = {
    de_net: results.querySelector('[data-zk-tax-out="de_net"]'),
    de_tax: results.querySelector('[data-zk-tax-out="de_tax"]'),
    cy_net: results.querySelector('[data-zk-tax-out="cy_net"]'),
    cy_tax: results.querySelector('[data-zk-tax-out="cy_tax"]'),
    savings_abs: results.querySelector('[data-zk-tax-out="savings_abs"]'),
    savings_pct: results.querySelector('[data-zk-tax-out="savings_pct"]'),
  };

  const bars = {
    de: results.querySelector('[data-zk-tax-bar="de"]'),
    cy: results.querySelector('[data-zk-tax-bar="cy"]'),
  };

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();

    const profit = parseFloat(String(form.querySelector('[name="profit"]')?.value ?? ''), 10);
    const salary = parseFloat(String(form.querySelector('[name="salary"]')?.value ?? ''), 10);
    const distribution = parseFloat(String(form.querySelector('[name="distribution"]')?.value ?? '0'), 10);
    const gewRate = parseFloat(String(form.querySelector('[name="bundesland"]')?.value ?? '0.155'), 10);

    const modeRetain = form.querySelector(`#zk-tax-mode-retain-${pid}`);
    const mode = modeRetain?.checked ? 'retain' : 'distribute';

    if (!Number.isFinite(profit) || !Number.isFinite(salary)) {
      form.reportValidity();
      return;
    }

    const res = computeComparison({
      profit,
      salary,
      distribution: Number.isFinite(distribution) ? distribution : 0,
      gewRate: Number.isFinite(gewRate) ? gewRate : 0.155,
      mode,
    });

    if (outs.de_net) {
      outs.de_net.textContent = fmtEur(res.netOwnerDE);
    }
    if (outs.de_tax) {
      outs.de_tax.textContent = fmtEur(res.totalTaxDE);
    }
    if (outs.cy_net) {
      outs.cy_net.textContent = fmtEur(res.netOwnerCY);
    }
    if (outs.cy_tax) {
      outs.cy_tax.textContent = fmtEur(res.totalTaxCY);
    }
    if (outs.savings_abs) {
      outs.savings_abs.textContent = fmtEur(res.savingsAbs);
    }
    if (outs.savings_pct) {
      outs.savings_pct.textContent = fmtPct(res.savingsPct);
    }

    if (bars.de) {
      bars.de.style.width = `${Math.min(100, Math.max(0, res.barDe))}%`;
    }
    if (bars.cy) {
      bars.cy.style.width = `${Math.min(100, Math.max(0, res.barCy))}%`;
    }

    results.classList.remove('zk-tax-calculator__results--hidden');
    results.removeAttribute('hidden');
    results.classList.toggle('zk-tax-calculator__results--negative', res.savingsAbs < 0);

    results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    results.focus({ preventScroll: true });
  });
}

export function initZkTaxCalculators() {
  document.querySelectorAll('[data-zk-tax-calculator]').forEach(initTaxCalculator);
}
